import { create } from 'zustand';
import { Status, StatusCreate, StatusUpdate } from '../types/common';
import api from '../config/axios';
import { STATUS } from '../config/apiPaths';

interface StatusStore {
  statuses: Status[];
  currentStatus: Status | null;
  isLoading: boolean;
  error: string | null;

  fetchStatuses: () => Promise<void>;
  fetchStatusById: (id: number) => Promise<void>;
  createStatus: (status: StatusCreate) => Promise<void>;
  updateStatus: (id: number, status: StatusUpdate) => Promise<void>;
  deleteStatus: (id: number) => Promise<void>;

  getStatusById: (id: number) => Status | undefined;
  getStatusByNameAndType: (name: string, type: string) => Status | undefined;
  getStatusIdByNameAndType: (name: string, type: string) => number | undefined;
  clearError: () => void;
}

export const useStatusStore = create<StatusStore>((set, get) => ({
  statuses: [],
  currentStatus: null,
  isLoading: false,
  error: null,

  fetchStatuses: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(STATUS.ALL);
      
      // Handle different response structures
      let statuses: Status[] = [];
      if (Array.isArray(response.data)) {
        statuses = response.data as Status[];
      } else if (response.data?.data) {
        // Handle case where data is nested under a data property
        statuses = Array.isArray(response.data.data) ? (response.data.data as Status[]) : [];
      }
      
      set({ statuses, isLoading: false });
      return;
    } catch (error: any) {
      // Log detailed error information
      console.error('Error fetching statuses:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        } : 'No response',
        request: error.request,
        config: error.config
      });
      
      set({ 
        error: error.response?.data?.message || 'Failed to fetch statuses', 
        isLoading: false 
      });
      
      // Don't return anything (void)
    }
  },

  fetchStatusById: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(STATUS.BY_ID(id));
      set((state) => ({
        statuses: [...state.statuses.filter(s => s.id !== id), response.data],
        currentStatus: response.data,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error fetching status by ID:', error);
      set({ error: 'Failed to fetch status', isLoading: false });
    }
  },

  createStatus: async (status: StatusCreate) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(STATUS.ALL, status);
      set((state) => ({
        statuses: [...state.statuses, response.data],
        isLoading: false
      }));
    } catch (error) {
      console.error('Error creating status:', error);
      set({ error: 'Failed to create status', isLoading: false });
    }
  },

  updateStatus: async (id: number, status: StatusUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put(STATUS.BY_ID(id), status);
      set((state) => ({
        statuses: state.statuses.map(s => s.id === id ? response.data : s),
        currentStatus: state.currentStatus?.id === id ? response.data : state.currentStatus,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error updating status:', error);
      set({ error: 'Failed to update status', isLoading: false });
    }
  },

  deleteStatus: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(STATUS.BY_ID(id));
      set((state) => ({
        statuses: state.statuses.filter(s => s.id !== id),
        isLoading: false
      }));
    } catch (error) {
      console.error('Error deleting status:', error);
      set({ error: 'Failed to delete status', isLoading: false });
    }
  },

  getStatusById: (id: number) => get().statuses.find(s => s.id === id),

  // Get status by name and type (useful for multi-tenant systems where IDs vary)
  getStatusByNameAndType: (name: string, type: string) => 
    get().statuses.find(s => s.name === name && s.type === type),
  
  // Get status ID by name and type (useful for multi-tenant systems where IDs vary)
  getStatusIdByNameAndType: (name: string, type: string) => {
    const status = get().statuses.find(s => s.name === name && s.type === type);
    return status?.id;
  },
  
  clearError: () => set({ error: null }),
}));